import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import api, { formatError } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { WalletTransfer } from '@/components/WalletTransfer';
import {
  TrendingUp, ArrowUpRight, ArrowDownRight, Loader2,
  ChevronLeft, Zap, AlertTriangle, X, Plus, Users, FileText
} from 'lucide-react';

const fmt = (n, d = 2) => `$${Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: d, minimumFractionDigits: d })}`;
const fmtP = (p) => { const n = Number(p || 0); if (n === 0) return '—'; if (n >= 1000) return n.toFixed(2); if (n >= 1) return n.toFixed(3); return n.toFixed(6); };

export default function FuturesTradingPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const isInvestor = user?.role === 'investor';
  const isAdmin = ['super_admin', 'admin', 'manager', 'trader'].includes(user?.role);

  const [symbols, setSymbols] = useState([]);
  const [prices, setPrices] = useState({});
  const [selected, setSelected] = useState('BTC');
  const [positions, setPositions] = useState([]);
  const [history, setHistory] = useState([]);
  const [clients, setClients] = useState([]);
  const [selectedClient, setSelectedClient] = useState('');
  const [clientBalance, setClientBalance] = useState(0);
  const [side, setSide] = useState('long');
  const [leverage, setLeverage] = useState(10);
  const [margin, setMargin] = useState('');
  const [sizeMode, setSizeMode] = useState('margin'); // 'margin' or 'qty'
  const [qtyInput, setQtyInput] = useState('');
  const [tp, setTp] = useState('');
  const [sl, setSl] = useState('');
  const [trailSl, setTrailSl] = useState('');   // absolute price distance for trailing SL
  const [executing, setExecuting] = useState(false);
  const [loading, setLoading] = useState(true);
  // Manual entry (admin/trader)
  const [manualDialog, setManualDialog] = useState(false);
  const [manualForm, setManualForm] = useState({
    client_id: '', symbol: 'BTC', side: 'long', leverage: 10,
    entry_price: '', exit_price: '', margin_usdt: '',
    realized_pnl: '', pnl_mode: 'auto',
    opened_at: '', closed_at: '',
    notes: '', auto_distribute: true, affect_balance: true,
  });
  const [allHistory, setAllHistory] = useState([]);
  const [allHistoryFilter, setAllHistoryFilter] = useState({ symbol: 'all', side: 'all' });
  const [loadingAllHistory, setLoadingAllHistory] = useState(false);
  const [symSearch, setSymSearch] = useState('');
  const symSearchRef = useRef(null);
  const [stats, setStats] = useState(null);
  const chartRef = useRef(null);

  const ep = useMemo(() => ({
    positions: isInvestor ? '/futures/me/positions' : (cid) => `/futures/positions/${cid}`,
    history: isInvestor ? '/futures/me/history' : (cid) => `/futures/history/${cid}`,
  }), [isInvestor]);

  const sym = useMemo(() => symbols.find(s => s.symbol === selected), [symbols, selected]);
  const maxLev = sym?.max_leverage || 100;
  const currentPrice = prices[selected]?.price || 0;
  const change24h = prices[selected]?.change_24h || 0;
  // Two-way size binding: user enters EITHER margin OR quantity; the other is auto-derived
  const marginNum = sizeMode === 'margin'
    ? (parseFloat(margin) || 0)
    : ((parseFloat(qtyInput) || 0) * currentPrice / (leverage || 1));
  const qty = sizeMode === 'margin'
    ? (currentPrice > 0 ? (marginNum * leverage) / currentPrice : 0)
    : (parseFloat(qtyInput) || 0);
  const notional = qty * currentPrice;
  // Unit label per symbol
  const unitLabel = ({
    XAUUSD: 'oz Gold', XAGUSD: 'oz Silver', USOIL: 'bbl Oil', NGAS: 'MMBtu',
    BTC: 'BTC', ETH: 'ETH', BNB: 'BNB', SOL: 'SOL', XRP: 'XRP', DOGE: 'DOGE', ADA: 'ADA',
  }[selected] || selected);
  // Estimated liquidation price (preview)
  const mmr = (sym?.maintenance_margin_pct || 0.5) / 100;
  const liqPx = currentPrice > 0
    ? (side === 'long'
        ? currentPrice * (1 - 1 / leverage + mmr)
        : currentPrice * (1 + 1 / leverage - mmr))
    : 0;

  // Live per-position mark, PnL and ROE derived from streaming prices
  const liveOf = useCallback((p) => {
    const mark = prices[p.symbol]?.price ?? p.current_price ?? p.entry_price ?? 0;
    const q = p.quantity ?? 0;
    const pnl = p.side === 'long' ? (mark - (p.entry_price || 0)) * q : ((p.entry_price || 0) - mark) * q;
    const roe = p.margin_usdt ? (pnl / p.margin_usdt) * 100 : 0;
    return { mark, pnl, roe };
  }, [prices]);

  // Quick symbol search — filter chips + `/` keyboard shortcut to focus
  const filteredSymbols = useMemo(() => {
    const q = symSearch.trim().toLowerCase();
    if (!q) return symbols;
    return symbols.filter(s => (s.symbol || '').toLowerCase().includes(q) || (s.name || '').toLowerCase().includes(q));
  }, [symbols, symSearch]);

  useEffect(() => {
    const onKey = (e) => {
      const tag = (e.target?.tagName || '').toLowerCase();
      if (tag === 'input' || tag === 'textarea' || e.target?.isContentEditable) return;
      if (e.key === '/') {
        e.preventDefault();
        symSearchRef.current?.focus();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const onSymSearchKeyDown = (e) => {
    if (e.key === 'Enter' && filteredSymbols[0]) {
      e.preventDefault();
      setSelected(filteredSymbols[0].symbol);
      setSymSearch('');
      symSearchRef.current?.blur();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      setSymSearch('');
      symSearchRef.current?.blur();
    }
  };

  const fetchPrices = useCallback(async () => {
    try {
      const { data } = await api.get('/trading/prices');
      setPrices(data);
    } catch (e) { console.error('[Futures] prices:', e); }
  }, []);

  const reloadAccount = useCallback(async (cid) => {
    const clientId = cid || selectedClient;
    if (!isInvestor && !clientId) return;
    const posUrl = isInvestor ? ep.positions : ep.positions(clientId);
    const histUrl = isInvestor ? ep.history : ep.history(clientId);
    const [pRes, hRes, sRes] = await Promise.all([
      api.get(posUrl).catch(() => ({ data: [] })),
      api.get(histUrl).catch(() => ({ data: [] })),
      api.get('/futures/stats?days=30').catch(() => ({ data: null })),
    ]);
    setPositions(pRes.data || []);
    setHistory(hRes.data || []);
    setStats(sRes.data);
    if (!isInvestor && clientId) {
      try {
        const { data } = await api.get(`/clients/${clientId}`);
        setClientBalance(data.current_balance || 0);
      } catch (e) { console.error('[Futures] client balance:', e); }
    } else if (isInvestor) {
      try {
        const { data } = await api.get('/wallet/balances');
        setClientBalance(data.futures_balance || 0);
      } catch (e) { console.error('[Futures] my balance:', e); }
    }
  }, [isInvestor, selectedClient, ep]);

  const init = useCallback(async () => {
    try {
      const [sRes, pRes] = await Promise.all([api.get('/futures/symbols'), api.get('/trading/prices')]);
      setSymbols(sRes.data); setPrices(pRes.data);
      if (!isInvestor && isAdmin) {
        const cRes = await api.get('/clients').catch(() => ({ data: [] }));
        setClients(cRes.data);
        if (cRes.data?.[0]) {
          setSelectedClient(cRes.data[0].id);
          setClientBalance(cRes.data[0].current_balance || 0);
        }
      }
    } catch (e) { console.error(e); toast.error('Failed to load futures market'); }
    finally { setLoading(false); }
  }, [isInvestor, isAdmin]);

  useEffect(() => { init(); }, [init]);
  useEffect(() => { reloadAccount(selectedClient); }, [selectedClient, reloadAccount]);
  // Fast price tick (5s) for live PnL/mark on positions
  useEffect(() => { const t = setInterval(fetchPrices, 5000); return () => clearInterval(t); }, [fetchPrices]);
  // Slower account reload (15s) — positions/history/balance
  useEffect(() => { const t = setInterval(reloadAccount, 15000); return () => clearInterval(t); }, [reloadAccount]);

  // TradingView dark chart
  useEffect(() => {
    const container = chartRef.current;
    if (!container) return;
    // Safe DOM clear (no innerHTML — avoids XSS surface)
    while (container.firstChild) container.removeChild(container.firstChild);
    const tvSym = selected === 'XAUUSD' ? 'OANDA:XAUUSD'
      : selected === 'XAGUSD' ? 'OANDA:XAGUSD'
      : `BINANCE:${selected}USDT.P`;
    const script = document.createElement('script');
    script.src = 'https://s3.tradingview.com/external-embedding/embed-widget-advanced-chart.js';
    script.type = 'text/javascript';
    script.async = true;
    // textContent (never parsed as HTML) is safe for JSON config strings
    script.textContent = JSON.stringify({
      autosize: true, symbol: tvSym, interval: "15", timezone: "Asia/Kolkata",
      theme: "dark", style: "1", locale: "en",
      hide_top_toolbar: false, hide_legend: false, allow_symbol_change: false,
      save_image: false, calendar: false, hide_volume: false, withdateranges: true,
      details: false, hotlist: false, watchlist: [], news: [],
      studies: [
        "Volume@tv-basicstudies",
        "MASimple@tv-basicstudies",
        "MAExp@tv-basicstudies",
        "BB@tv-basicstudies",
      ],
      studies_overrides: {
        "Moving Average.length": 20,
        "Moving Average.plot.color": "#3b82f6",
        "Moving Average.plot.linewidth": 2,
        "Moving Average Exponential.length": 50,
        "Moving Average Exponential.plot.color": "#f59e0b",
        "Moving Average Exponential.plot.linewidth": 2,
        "Bollinger Bands.median.color": "#a855f7",
        "Bollinger Bands.upper.color": "#a855f7",
        "Bollinger Bands.lower.color": "#a855f7",
        "volume.volume.color.0": "#ef4444",
        "volume.volume.color.1": "#10b981",
        "volume.volume.transparency": 55,
      },
      overrides: {
        "mainSeriesProperties.candleStyle.upColor": "#10b981",
        "mainSeriesProperties.candleStyle.downColor": "#ef4444",
        "mainSeriesProperties.candleStyle.borderUpColor": "#10b981",
        "mainSeriesProperties.candleStyle.borderDownColor": "#ef4444",
        "mainSeriesProperties.candleStyle.wickUpColor": "#10b981",
        "mainSeriesProperties.candleStyle.wickDownColor": "#ef4444",
        "paneProperties.background": "#0f172a",
        "paneProperties.vertGridProperties.color": "rgba(71, 85, 105, 0.15)",
        "paneProperties.horzGridProperties.color": "rgba(71, 85, 105, 0.15)",
        "scalesProperties.textColor": "#94a3b8",
      },
      backgroundColor: "rgba(15, 23, 42, 1)", gridColor: "rgba(71, 85, 105, 0.15)",
    });
    const c = document.createElement('div');
    c.className = 'tradingview-widget-container__widget';
    c.style.height = '100%'; c.style.width = '100%';
    container.appendChild(c);
    container.appendChild(script);
    return () => {
      // Cleanup on unmount / re-run so StrictMode double-invoke can re-init cleanly
      while (container.firstChild) container.removeChild(container.firstChild);
    };
  }, [selected]);

  const handleOpen = async () => {
    if (!isInvestor && !selectedClient) { toast.error('Select a client'); return; }
    if (marginNum <= 0) {
      toast.error(sizeMode === 'qty' ? `Enter quantity in ${unitLabel}` : 'Enter margin amount');
      return;
    }
    if (marginNum > clientBalance) { toast.error(`Insufficient balance — this order needs $${marginNum.toFixed(2)}`); return; }
    setExecuting(true);
    try {
      const payload = {
        symbol: selected, side, leverage,
        margin_usdt: Number(marginNum.toFixed(2)),
        tp_price: tp ? parseFloat(tp) : null,
        sl_price: sl ? parseFloat(sl) : null,
        trailing_sl_distance: trailSl ? parseFloat(trailSl) : null,
      };
      if (!isInvestor) payload.client_id = selectedClient;
      const { data } = await api.post('/futures/open', payload);
      toast.success(`${side.toUpperCase()} ${selected} ${leverage}x · entry $${fmtP(data.entry_price)} · liq $${fmtP(data.liquidation_price)}`);
      setMargin(''); setQtyInput(''); setTp(''); setSl(''); setTrailSl('');
      await reloadAccount();
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
    finally { setExecuting(false); }
  };

  const handleClose = async (pid, closePct = 100) => {
    setExecuting(true);
    try {
      const { data } = await api.post(`/futures/close/${pid}?close_pct=${closePct}`);
      const pnl = data.realized_pnl;
      toast.success(`${closePct < 100 ? `Partial ${closePct}%` : 'Closed'} @ $${fmtP(data.exit_price)} · PnL ${pnl >= 0 ? '+' : ''}$${pnl.toFixed(2)}`);
      await reloadAccount();
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
    finally { setExecuting(false); }
  };

  const loadAllHistory = useCallback(async () => {
    setLoadingAllHistory(true);
    try {
      const params = {};
      if (allHistoryFilter.symbol !== 'all') params.symbol = allHistoryFilter.symbol;
      if (allHistoryFilter.side !== 'all') params.side = allHistoryFilter.side;
      const { data } = await api.get('/futures/all-history', { params });
      setAllHistory(data || []);
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail) || 'Failed to load history');
    } finally {
      setLoadingAllHistory(false);
    }
  }, [allHistoryFilter]);

  useEffect(() => { if (!isInvestor && isAdmin) loadAllHistory(); }, [loadAllHistory, isInvestor, isAdmin]);

  // Auto-calc P&L preview for manual entry
  const manualPnlPreview = useMemo(() => {
    const ep = parseFloat(manualForm.entry_price);
    const xp = parseFloat(manualForm.exit_price);
    const marg = parseFloat(manualForm.margin_usdt);
    const lev = parseInt(manualForm.leverage) || 1;
    if (!ep || !xp || !marg) return { pnl: 0, roe: 0, qty: 0 };
    const qty = (marg * lev) / ep;
    const pnl = manualForm.side === 'long' ? (xp - ep) * qty : (ep - xp) * qty;
    const roe = (pnl / marg) * 100;
    return { pnl: Math.round(pnl * 100) / 100, roe: Math.round(roe * 100) / 100, qty };
  }, [manualForm.entry_price, manualForm.exit_price, manualForm.margin_usdt, manualForm.leverage, manualForm.side]);

  const handleManualSubmit = async () => {
    if (!manualForm.client_id) { toast.error('Select a client'); return; }
    if (!manualForm.entry_price || !manualForm.exit_price || !manualForm.margin_usdt) {
      toast.error('Entry, exit and margin are required');
      return;
    }
    setExecuting(true);
    try {
      const payload = {
        client_id: manualForm.client_id,
        symbol: manualForm.symbol,
        side: manualForm.side,
        leverage: parseInt(manualForm.leverage),
        entry_price: parseFloat(manualForm.entry_price),
        exit_price: parseFloat(manualForm.exit_price),
        margin_usdt: parseFloat(manualForm.margin_usdt),
        auto_distribute: manualForm.auto_distribute,
        affect_balance: manualForm.affect_balance,
        notes: manualForm.notes,
      };
      if (manualForm.pnl_mode === 'manual' && manualForm.realized_pnl !== '') {
        payload.realized_pnl = parseFloat(manualForm.realized_pnl);
      }
      if (manualForm.opened_at) payload.opened_at = new Date(manualForm.opened_at).toISOString();
      if (manualForm.closed_at) payload.closed_at = new Date(manualForm.closed_at).toISOString();
      const { data } = await api.post('/futures/manual-entry', payload);
      toast.success(`Recorded ${data.side.toUpperCase()} ${data.symbol} ${data.leverage}x · PnL ${data.realized_pnl >= 0 ? '+' : ''}$${data.realized_pnl}`);
      setManualDialog(false);
      setManualForm({
        ...manualForm, entry_price: '', exit_price: '', margin_usdt: '',
        realized_pnl: '', notes: '', opened_at: '', closed_at: '',
      });
      await Promise.all([reloadAccount(), loadAllHistory()]);
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail) || 'Failed to record manual trade');
    } finally {
      setExecuting(false);
    }
  };

  if (loading) return <div className="flex items-center justify-center h-64 bg-slate-950"><Loader2 size={24} className="animate-spin text-orange-500" /></div>;

  // Wrap entire page in dark theme
  return (
    <div className="min-h-screen bg-slate-950 text-slate-100 p-3 sm:p-4" data-testid="futures-page">
      <div className="max-w-[1700px] mx-auto space-y-3">
        {/* Top ticker */}
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div className="flex items-center gap-3">
            {isInvestor && (
              <Button variant="ghost" size="sm" onClick={() => navigate('/investor')} className="text-slate-300 hover:bg-slate-800 h-8" data-testid="back-btn">
                <ChevronLeft size={14} className="mr-1" /> Portal
              </Button>
            )}
            <h1 className="font-heading text-xl sm:text-2xl font-bold flex items-center gap-2">
              <Zap className="text-orange-500" size={20} /> Futures
              <Badge className="bg-orange-500/15 text-orange-400 border-0 text-[10px] ml-1">UP TO 100x</Badge>
            </h1>
          </div>
          {!isInvestor && isAdmin && (
            <Select value={selectedClient} onValueChange={setSelectedClient}>
              <SelectTrigger className="w-56 bg-slate-900 border-slate-700 text-slate-100 h-8 text-xs" data-testid="futures-client-select">
                <SelectValue placeholder="Select client" />
              </SelectTrigger>
              <SelectContent className="bg-slate-900 border-slate-700 text-slate-100">{clients.map(c => <SelectItem key={c.id} value={c.id}>{c.client_id} — {c.name}</SelectItem>)}</SelectContent>
            </Select>
          )}
          {!isInvestor && isAdmin && (
            <Button size="sm" onClick={() => { setManualForm({ ...manualForm, client_id: selectedClient || (clients[0]?.id ?? '') }); setManualDialog(true); }}
              className="bg-orange-500 hover:bg-orange-600 text-white h-8 text-xs" data-testid="manual-entry-btn">
              <Plus size={12} className="mr-1" /> Manual Entry
            </Button>
          )}
          {isInvestor && <WalletTransfer dark triggerLabel="Transfer" onDone={() => reloadAccount()} />}
        </div>

        {/* Asset ticker bar */}
        <div className="bg-slate-900 border border-slate-800 rounded-lg p-2 overflow-x-auto">
          <div className="flex gap-3 min-w-max">
            {symbols.slice(0, 8).map(s => {
              const p = prices[s.symbol];
              const ch = p?.change_24h || 0;
              return (
                <button key={s.symbol} onClick={() => setSelected(s.symbol)}
                  className={`flex items-center gap-2 px-2 py-1 rounded transition-colors ${selected === s.symbol ? 'bg-orange-500/15 text-orange-400' : 'hover:bg-slate-800 text-slate-200'}`}
                  data-testid={`ticker-${s.symbol}`}>
                  <span className="text-xs font-bold">{s.symbol}USDT</span>
                  <span className="text-xs font-mono">${fmtP(p?.price)}</span>
                  <span className={`text-[10px] font-semibold ${ch >= 0 ? 'text-green-400' : 'text-red-400'}`}>{ch >= 0 ? '+' : ''}{ch.toFixed(2)}%</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Main grid */}
        <div className="grid grid-cols-1 xl:grid-cols-4 gap-3">
          {/* Chart + price column */}
          <div className="xl:col-span-3 space-y-3">
            <div className="bg-slate-900 border border-slate-800 rounded-lg p-3 flex items-center justify-between flex-wrap gap-3">
              <div className="flex items-center gap-4">
                <div>
                  <div className="flex items-center gap-2">
                    <span className="font-heading text-lg font-bold">{selected}</span>
                    <Badge className="bg-yellow-500/15 text-yellow-400 border-0 text-[9px]">{sym?.max_leverage}x MAX</Badge>
                  </div>
                  <p className="text-[10px] text-slate-400">{sym?.name}</p>
                </div>
                <div>
                  <p className={`font-heading text-2xl font-bold tabular-nums ${change24h >= 0 ? 'text-green-400' : 'text-red-400'}`}>${fmtP(currentPrice)}</p>
                  <p className={`text-[10px] font-semibold ${change24h >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                    {change24h >= 0 ? <ArrowUpRight size={10} className="inline" /> : <ArrowDownRight size={10} className="inline" />} {change24h >= 0 ? '+' : ''}{change24h.toFixed(2)}% 24h
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <div className="relative flex-shrink-0">
                  <input
                    ref={symSearchRef}
                    value={symSearch}
                    onChange={e => setSymSearch(e.target.value)}
                    onKeyDown={onSymSearchKeyDown}
                    placeholder="Search / "
                    className="bg-slate-800 border border-slate-700 focus:border-orange-500 outline-none text-[10px] text-slate-200 placeholder:text-slate-500 rounded pl-2 pr-6 py-1 w-[100px] font-mono"
                    data-testid="symbol-search-input"
                  />
                  {symSearch && (
                    <button onClick={() => { setSymSearch(''); symSearchRef.current?.focus(); }} className="absolute right-1 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-200" data-testid="symbol-search-clear">
                      <X size={10} />
                    </button>
                  )}
                </div>
                <div className="flex gap-1.5 flex-wrap flex-1" data-testid="symbol-chips">
                  {filteredSymbols.length === 0 ? (
                    <span className="text-[10px] text-slate-500 italic py-1">No matching symbols</span>
                  ) : filteredSymbols.map(s => (
                    <button key={s.symbol} onClick={() => setSelected(s.symbol)} className={`text-[10px] font-bold px-2 py-1 rounded transition-colors ${selected === s.symbol ? 'bg-orange-500 text-white' : 'bg-slate-800 text-slate-300 hover:bg-slate-700'}`} data-testid={`select-${s.symbol}`}>{s.symbol}</button>
                  ))}
                </div>
              </div>
            </div>

            {/* Chart */}
            <div className="bg-slate-900 border border-slate-800 rounded-lg overflow-hidden">
              <div className="relative" style={{ height: '500px', width: '100%' }}>
                <div className="absolute inset-0 flex items-center justify-center text-slate-500 text-xs z-0">
                  <Loader2 size={14} className="animate-spin mr-2" /> Loading chart…
                </div>
                <div ref={chartRef} className="tradingview-widget-container relative z-10" style={{ height: '500px', width: '100%' }} data-testid="futures-tv-chart" />

                {/* Chart overlay: open positions for current symbol with quick close/partial */}
                {positions.filter(p => p.symbol === selected).length > 0 && (
                  <div className="absolute top-3 right-3 z-30 bg-slate-950/90 backdrop-blur border border-slate-700 rounded-lg p-2 max-w-[300px] shadow-xl" data-testid="chart-positions-overlay">
                    <p className="text-[9px] uppercase tracking-widest font-bold text-slate-400 mb-1.5 flex items-center gap-1">
                      <Zap size={10} className="text-orange-400" /> On chart · {positions.filter(p => p.symbol === selected).length}
                    </p>
                    <div className="space-y-1.5">
                      {positions.filter(p => p.symbol === selected).map(p => {
                        const live = liveOf(p);
                        return (
                          <div key={p.id} className="bg-slate-900 rounded p-1.5 border border-slate-800" data-testid={`chart-pos-${p.id}`}>
                            <div className="flex items-center gap-1 mb-0.5 text-[10px]">
                              <Badge className={`text-[8px] border-0 h-4 px-1 ${p.side === 'long' ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'}`}>{p.side.toUpperCase()} {p.leverage}x</Badge>
                              <span className="font-mono text-slate-400">{p.quantity?.toFixed(4)}</span>
                              <span className={`font-mono font-bold ml-auto ${live.pnl >= 0 ? 'text-green-400' : 'text-red-400'}`}>{live.pnl >= 0 ? '+' : ''}${live.pnl.toFixed(2)}</span>
                            </div>
                            <div className="grid grid-cols-3 gap-0.5 text-[9px] mb-1">
                              <div className="bg-slate-800 px-1 py-0.5 rounded"><span className="text-slate-500">E</span> <span className="font-mono">${fmtP(p.entry_price)}</span></div>
                              {p.tp_price ? <div className="bg-green-500/10 px-1 py-0.5 rounded"><span className="text-green-500">TP</span> <span className="font-mono">${fmtP(p.tp_price)}</span></div> : <div className="bg-slate-800 px-1 py-0.5 rounded text-slate-600">TP —</div>}
                              {p.sl_price ? <div className="bg-red-500/10 px-1 py-0.5 rounded flex items-center gap-0.5">
                                {p.trailing_sl_distance ? <span title="Trailing" className="text-orange-400">↑</span> : null}
                                <span className="text-red-500">SL</span> <span className="font-mono">${fmtP(p.sl_price)}</span>
                              </div> : <div className="bg-slate-800 px-1 py-0.5 rounded text-slate-600">SL —</div>}
                            </div>
                            <div className="flex gap-0.5">
                              {[25, 50, 100].map(pct => (
                                <Button key={pct} size="sm" onClick={() => handleClose(p.id, pct)} disabled={executing}
                                  className={`flex-1 h-5 text-[9px] px-0 ${pct === 100 ? 'bg-red-500/80 hover:bg-red-500 text-white' : 'bg-slate-700 hover:bg-slate-600 text-slate-200'}`}
                                  data-testid={`chart-close-${p.id}-${pct}`}>{pct === 100 ? 'Close' : `${pct}%`}</Button>
                              ))}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Order panel */}
          <div className="space-y-3">
            <div className="bg-slate-900 border border-slate-800 rounded-lg p-3 space-y-3">
              {/* Margin mode (cross only for now) + Long/Short toggle */}
              <div className="flex gap-1">
                <button onClick={() => setSide('long')} className={`flex-1 py-2 rounded text-xs font-bold transition-colors ${side === 'long' ? 'bg-green-500 text-white' : 'bg-slate-800 text-slate-400 hover:bg-slate-700'}`} data-testid="open-long-btn">
                  Open Long
                </button>
                <button onClick={() => setSide('short')} className={`flex-1 py-2 rounded text-xs font-bold transition-colors ${side === 'short' ? 'bg-red-500 text-white' : 'bg-slate-800 text-slate-400 hover:bg-slate-700'}`} data-testid="open-short-btn">
                  Open Short
                </button>
              </div>

              {/* Leverage slider */}
              <div>
                <div className="flex justify-between text-[10px] uppercase tracking-wider text-slate-400 font-semibold mb-1.5">
                  <span>Leverage</span>
                  <span className="text-orange-400 font-mono">{leverage}x</span>
                </div>
                <input type="range" min="1" max={maxLev} value={leverage} onChange={e => setLeverage(parseInt(e.target.value))}
                  className="w-full accent-orange-500" data-testid="leverage-slider" />
                <div className="flex justify-between gap-1 mt-1">
                  {[1, 5, 10, 25, 50, 75, 100].filter(v => v <= maxLev).map(v => (
                    <button key={v} onClick={() => setLeverage(v)} className={`flex-1 py-0.5 text-[9px] rounded font-mono font-bold ${leverage === v ? 'bg-orange-500 text-white' : 'bg-slate-800 text-slate-400 hover:bg-slate-700'}`} data-testid={`lev-${v}`}>{v}x</button>
                  ))}
                </div>
              </div>

              {/* Size (mode toggle: Margin USDT ↔ Quantity units) */}
              <div>
                <div className="flex items-center justify-between mb-1">
                  <div className="flex bg-slate-800 rounded p-0.5 text-[9px]" data-testid="size-mode-toggle">
                    <button onClick={() => setSizeMode('margin')} className={`px-2 py-0.5 rounded font-bold ${sizeMode === 'margin' ? 'bg-orange-500 text-white' : 'text-slate-400 hover:text-slate-200'}`} data-testid="size-mode-margin">USDT</button>
                    <button onClick={() => setSizeMode('qty')} className={`px-2 py-0.5 rounded font-bold ${sizeMode === 'qty' ? 'bg-orange-500 text-white' : 'text-slate-400 hover:text-slate-200'}`} data-testid="size-mode-qty">{unitLabel}</button>
                  </div>
                  <span className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">Bal: ${clientBalance.toFixed(2)}</span>
                </div>
                {sizeMode === 'margin' ? (
                  <>
                    <Input type="number" step="any" value={margin} onChange={e => setMargin(e.target.value)} placeholder="Margin in USDT"
                      className="bg-slate-800 border-slate-700 text-slate-100 h-9 text-sm font-mono" data-testid="margin-input" />
                    <div className="grid grid-cols-4 gap-1 mt-1">
                      {[10, 25, 50, 100].map(p => (
                        <button key={p} onClick={() => setMargin((clientBalance * p / 100).toFixed(2))}
                          className="py-0.5 text-[9px] bg-slate-800 hover:bg-slate-700 text-slate-300 rounded font-bold" data-testid={`margin-pct-${p}`}>{p}%</button>
                      ))}
                    </div>
                  </>
                ) : (
                  <>
                    <Input type="number" step="any" value={qtyInput} onChange={e => setQtyInput(e.target.value)} placeholder={`Quantity (${unitLabel})`}
                      className="bg-slate-800 border-slate-700 text-slate-100 h-9 text-sm font-mono" data-testid="qty-input" />
                    <div className="grid grid-cols-4 gap-1 mt-1">
                      {[1, 5, 10, 100].map(q => (
                        <button key={q} onClick={() => setQtyInput(String(q))}
                          className="py-0.5 text-[9px] bg-slate-800 hover:bg-slate-700 text-slate-300 rounded font-bold" data-testid={`qty-preset-${q}`}>{q}</button>
                      ))}
                    </div>
                  </>
                )}
              </div>

              {/* Position summary */}
              <div className="bg-slate-950 rounded p-2 space-y-1 text-[10px]" data-testid="position-preview">
                <div className="flex justify-between"><span className="text-slate-400">Position Size</span><span className="font-mono">${notional.toFixed(2)}</span></div>
                <div className="flex justify-between"><span className="text-slate-400">Qty</span><span className="font-mono">{qty.toFixed(6)} {unitLabel}</span></div>
                <div className="flex justify-between"><span className="text-slate-400">Margin</span><span className="font-mono">${marginNum.toFixed(2)}</span></div>
                <div className="flex justify-between"><span className="text-slate-400">Est. Liq. Price</span><span className="font-mono font-bold text-red-400">${fmtP(liqPx)}</span></div>
              </div>

              {/* TP/SL */}
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="text-[10px] uppercase tracking-wider text-green-400 font-semibold mb-1 block">Take Profit</label>
                  <Input type="number" step="any" value={tp} onChange={e => setTp(e.target.value)} placeholder="Optional"
                    className="bg-slate-800 border-slate-700 text-slate-100 h-8 text-xs font-mono" data-testid="tp-input" />
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-wider text-red-400 font-semibold mb-1 block">Stop Loss</label>
                  <Input type="number" step="any" value={sl} onChange={e => setSl(e.target.value)} placeholder="Optional"
                    className="bg-slate-800 border-slate-700 text-slate-100 h-8 text-xs font-mono" data-testid="sl-input" />
                </div>
              </div>

              {/* Trailing SL */}
              <div>
                <label className="text-[10px] uppercase tracking-wider text-orange-400 font-semibold mb-1 flex items-center justify-between">
                  <span>Trailing SL Distance ($)</span>
                  {trailSl && Number(trailSl) > 0 && currentPrice > 0 && (
                    <span className="text-slate-400 normal-case tracking-normal">≈ SL @ ${fmtP(side === 'long' ? currentPrice - Number(trailSl) : currentPrice + Number(trailSl))}</span>
                  )}
                </label>
                <Input type="number" step="any" value={trailSl} onChange={e => setTrailSl(e.target.value)} placeholder="Optional — SL auto-follows price"
                  className="bg-slate-800 border-slate-700 text-slate-100 h-8 text-xs font-mono" data-testid="trailing-sl-input" />
              </div>

              <Button onClick={handleOpen} disabled={executing || marginNum <= 0 || marginNum > clientBalance}
                className={`w-full h-10 rounded font-bold text-sm ${side === 'long' ? 'bg-green-500 hover:bg-green-600' : 'bg-red-500 hover:bg-red-600'} text-white disabled:opacity-40`}
                data-testid="open-position-btn">
                {executing ? <Loader2 size={14} className="animate-spin" /> : `${side === 'long' ? 'Open Long' : 'Open Short'} ${qty > 0 ? qty.toFixed(4) : ''} ${unitLabel}`}
              </Button>
            </div>
          </div>
        </div>

        {/* Trader stats — win-rate + 30-day heatmap */}
        {stats && stats.trade_count > 0 && (
          <div className="grid grid-cols-2 lg:grid-cols-6 gap-2 mb-3" data-testid="futures-stats">
            <StatTile label="Trades" value={stats.trade_count} />
            <StatTile label="Win Rate" value={`${stats.win_rate}%`} tone={stats.win_rate >= 50 ? 'ok' : 'bad'} />
            <StatTile label="Total PnL" value={`${stats.total_pnl >= 0 ? '+' : ''}$${stats.total_pnl.toFixed(2)}`} tone={stats.total_pnl >= 0 ? 'ok' : 'bad'} />
            <StatTile label="Avg Win" value={`$${stats.avg_win.toFixed(2)}`} tone="ok" />
            <StatTile label="Avg Loss" value={`$${stats.avg_loss.toFixed(2)}`} tone="bad" />
            <StatTile label="Best / Worst" value={`+$${stats.best.toFixed(0)} · $${stats.worst.toFixed(0)}`} />
            <div className="col-span-2 lg:col-span-6 bg-slate-900 border border-slate-800 rounded-lg p-2 overflow-x-auto" data-testid="pnl-heatmap">
              <p className="text-[9px] uppercase tracking-widest text-slate-400 font-bold mb-1.5">30-day PnL heatmap</p>
              <div className="flex gap-0.5 min-w-max">
                {stats.daily.map(d => {
                  const intensity = Math.min(1, Math.abs(d.pnl) / (Math.max(1, Math.abs(stats.best))));
                  const bg = d.pnl >= 0
                    ? `rgba(16,185,129,${0.15 + intensity * 0.65})`
                    : `rgba(239,68,68,${0.15 + intensity * 0.65})`;
                  return (
                    <div key={d.date} title={`${d.date}: ${d.pnl >= 0 ? '+' : ''}$${d.pnl.toFixed(2)}`}
                      className="w-4 h-5 rounded-sm flex-shrink-0 border border-slate-800"
                      style={{ background: bg }} />
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {/* Positions & history */}
        <Tabs defaultValue="positions" className="bg-slate-900 border border-slate-800 rounded-lg">
          <TabsList className="bg-slate-800/40 border-b border-slate-800 rounded-t-lg rounded-b-none p-0 w-full justify-start">
            <TabsTrigger value="positions" className="data-[state=active]:bg-slate-900 data-[state=active]:text-orange-400 text-slate-400 text-xs rounded-none px-5 py-2.5">
              Open Positions {positions.length > 0 && `(${positions.length})`}
            </TabsTrigger>
            <TabsTrigger value="history" className="data-[state=active]:bg-slate-900 data-[state=active]:text-orange-400 text-slate-400 text-xs rounded-none px-5 py-2.5">
              History
            </TabsTrigger>
            {!isInvestor && isAdmin && (
              <TabsTrigger value="all-clients" className="data-[state=active]:bg-slate-900 data-[state=active]:text-orange-400 text-slate-400 text-xs rounded-none px-5 py-2.5" data-testid="tab-all-clients">
                <Users size={12} className="mr-1" /> All Clients {allHistory.length > 0 && `(${allHistory.length})`}
              </TabsTrigger>
            )}
          </TabsList>

          <TabsContent value="positions" className="m-0 p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-xs" data-testid="positions-table">
                <thead><tr className="border-b border-slate-800 bg-slate-800/30">
                  {['Symbol', 'Side', 'Lev', 'Size', 'Margin', 'Entry', 'Mark', 'Liq. Price', 'TP/SL', 'uPnL (ROE%)', 'Action'].map(h => <th key={h} className="text-left py-2.5 px-3 text-[10px] uppercase tracking-wider font-semibold text-slate-400">{h}</th>)}
                </tr></thead>
                <tbody>
                  {positions.length === 0 ? <tr><td colSpan={11} className="py-10 text-center text-slate-500">No open positions</td></tr> :
                   positions.map(p => {
                    const live = liveOf(p);
                    const pnlPos = live.pnl >= 0;
                    return (
                    <tr key={p.id} className="border-b border-slate-800 hover:bg-slate-800/40" data-testid={`position-row-${p.id}`}>
                      <td className="py-2.5 px-3 font-bold">{p.symbol}</td>
                      <td className="py-2.5 px-3">
                        <Badge className={`text-[9px] border-0 ${p.side === 'long' ? 'bg-green-500/15 text-green-400' : 'bg-red-500/15 text-red-400'}`}>{p.side.toUpperCase()}</Badge>
                      </td>
                      <td className="py-2.5 px-3 font-mono text-orange-400 font-bold">{p.leverage}x</td>
                      <td className="py-2.5 px-3 font-mono">{p.quantity?.toFixed(6)}</td>
                      <td className="py-2.5 px-3 font-mono">${p.margin_usdt?.toFixed(2)}</td>
                      <td className="py-2.5 px-3 font-mono">${fmtP(p.entry_price)}</td>
                      <td className="py-2.5 px-3 font-mono">
                        <span className="inline-flex items-center gap-1">
                          <span className="w-1.5 h-1.5 rounded-full bg-green-400 animate-pulse" title="Live" />
                          <span data-testid={`pos-mark-${p.id}`}>${fmtP(live.mark)}</span>
                        </span>
                      </td>
                      <td className="py-2.5 px-3 font-mono text-red-400 font-bold">
                        <span className="inline-flex items-center gap-1">
                          <AlertTriangle size={9} /> ${fmtP(p.liquidation_price)}
                        </span>
                      </td>
                      <td className="py-2.5 px-3 text-[10px]">
                        {p.tp_price ? <span className="text-green-400">TP ${fmtP(p.tp_price)}</span> : <span className="text-slate-600">—</span>}<br />
                        {p.sl_price ? <span className="text-red-400">SL ${fmtP(p.sl_price)}</span> : <span className="text-slate-600">—</span>}
                      </td>
                      <td className={`py-2.5 px-3 font-mono font-bold ${pnlPos ? 'text-green-400' : 'text-red-400'}`} data-testid={`pos-pnl-${p.id}`}>
                        {pnlPos ? '+' : ''}${live.pnl.toFixed(2)} <span className="text-[10px] opacity-80">({live.roe >= 0 ? '+' : ''}{live.roe.toFixed(2)}%)</span>
                      </td>
                      <td className="py-2.5 px-3">
                        <div className="flex gap-0.5" data-testid={`close-chips-${p.id}`}>
                          {[25, 50, 75].map(pct => (
                            <Button key={pct} size="sm" variant="outline" onClick={() => handleClose(p.id, pct)} disabled={executing}
                              className="h-6 text-[9px] px-1.5 border-slate-700 text-slate-300 hover:bg-slate-700"
                              data-testid={`close-${p.id}-${pct}`}>{pct}%</Button>
                          ))}
                          <Button size="sm" onClick={() => handleClose(p.id, 100)} disabled={executing}
                            className="h-6 text-[9px] px-2 bg-red-500/80 hover:bg-red-500 text-white"
                            data-testid={`close-${p.id}-100`}>Close</Button>
                        </div>
                      </td>
                    </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </TabsContent>

          <TabsContent value="history" className="m-0 p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-xs" data-testid="history-table">
                <thead><tr className="border-b border-slate-800 bg-slate-800/30">
                  {['Symbol', 'Side', 'Lev', 'Entry', 'Exit', 'Margin', 'PnL (ROE%)', 'Status', 'Closed'].map(h => <th key={h} className="text-left py-2.5 px-3 text-[10px] uppercase tracking-wider font-semibold text-slate-400">{h}</th>)}
                </tr></thead>
                <tbody>
                  {history.length === 0 ? <tr><td colSpan={9} className="py-10 text-center text-slate-500">No trade history</td></tr> :
                   history.map(p => (
                    <tr key={p.id} className="border-b border-slate-800 hover:bg-slate-800/40">
                      <td className="py-2.5 px-3 font-bold">{p.symbol}</td>
                      <td className="py-2.5 px-3"><Badge className={`text-[9px] border-0 ${p.side === 'long' ? 'bg-green-500/15 text-green-400' : 'bg-red-500/15 text-red-400'}`}>{p.side?.toUpperCase()}</Badge></td>
                      <td className="py-2.5 px-3 font-mono text-orange-400 font-bold">{p.leverage}x</td>
                      <td className="py-2.5 px-3 font-mono">${fmtP(p.entry_price)}</td>
                      <td className="py-2.5 px-3 font-mono">${fmtP(p.exit_price)}</td>
                      <td className="py-2.5 px-3 font-mono">${p.margin_usdt?.toFixed(2)}</td>
                      <td className={`py-2.5 px-3 font-mono font-bold ${(p.realized_pnl || 0) >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                        {(p.realized_pnl || 0) >= 0 ? '+' : ''}${p.realized_pnl?.toFixed(2)} ({p.roe_pct?.toFixed(1)}%)
                      </td>
                      <td className="py-2.5 px-3">
                        <Badge className={`text-[9px] border-0 ${p.status === 'liquidated' ? 'bg-red-500/20 text-red-400' : p.status === 'tp_hit' ? 'bg-green-500/20 text-green-400' : p.status === 'sl_hit' ? 'bg-yellow-500/20 text-yellow-400' : 'bg-slate-700 text-slate-300'}`}>
                          {p.status?.toUpperCase().replace('_', ' ')}
                        </Badge>
                      </td>
                      <td className="py-2.5 px-3 text-[10px] text-slate-500">{p.closed_at ? new Date(p.closed_at).toLocaleString() : '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </TabsContent>

          {!isInvestor && isAdmin && (
            <TabsContent value="all-clients" className="m-0 p-0" data-testid="all-clients-tab">
              {/* Filter bar */}
              <div className="flex flex-wrap items-center gap-2 p-3 border-b border-slate-800 bg-slate-800/20">
                <span className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold">Filters</span>
                <Select value={allHistoryFilter.symbol} onValueChange={v => setAllHistoryFilter({ ...allHistoryFilter, symbol: v })}>
                  <SelectTrigger className="w-28 bg-slate-900 border-slate-700 text-slate-100 h-8 text-xs" data-testid="filter-symbol">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-900 border-slate-700 text-slate-100">
                    <SelectItem value="all">All Symbols</SelectItem>
                    {symbols.map(s => <SelectItem key={s.symbol} value={s.symbol}>{s.symbol}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Select value={allHistoryFilter.side} onValueChange={v => setAllHistoryFilter({ ...allHistoryFilter, side: v })}>
                  <SelectTrigger className="w-24 bg-slate-900 border-slate-700 text-slate-100 h-8 text-xs" data-testid="filter-side">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-900 border-slate-700 text-slate-100">
                    <SelectItem value="all">All Sides</SelectItem>
                    <SelectItem value="long">Long</SelectItem>
                    <SelectItem value="short">Short</SelectItem>
                  </SelectContent>
                </Select>
                <Button size="sm" onClick={loadAllHistory} className="h-8 text-xs bg-slate-800 hover:bg-slate-700 text-slate-100 ml-auto" data-testid="refresh-all-history">
                  {loadingAllHistory ? <Loader2 size={12} className="animate-spin" /> : 'Refresh'}
                </Button>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-xs" data-testid="all-clients-table">
                  <thead><tr className="border-b border-slate-800 bg-slate-800/30">
                    {['Client', 'Symbol', 'Side', 'Lev', 'Entry', 'Exit', 'Margin', 'PnL (ROE%)', 'Source', 'Status', 'Closed'].map(h => <th key={h} className="text-left py-2.5 px-3 text-[10px] uppercase tracking-wider font-semibold text-slate-400">{h}</th>)}
                  </tr></thead>
                  <tbody>
                    {loadingAllHistory ? (
                      <tr><td colSpan={11} className="py-10 text-center text-slate-500"><Loader2 size={14} className="animate-spin inline mr-1" /> Loading…</td></tr>
                    ) : allHistory.length === 0 ? (
                      <tr><td colSpan={11} className="py-10 text-center text-slate-500">No closed futures trades yet across any client.</td></tr>
                    ) : allHistory.map(p => (
                      <tr key={p.id} className="border-b border-slate-800 hover:bg-slate-800/40" data-testid={`all-row-${p.id}`}>
                        <td className="py-2.5 px-3">
                          <p className="font-semibold text-slate-100">{p.client_name || '—'}</p>
                          <p className="text-[9px] text-slate-500 font-mono">{p.client_id?.slice(-6)}</p>
                        </td>
                        <td className="py-2.5 px-3 font-bold">{p.symbol}</td>
                        <td className="py-2.5 px-3"><Badge className={`text-[9px] border-0 ${p.side === 'long' ? 'bg-green-500/15 text-green-400' : 'bg-red-500/15 text-red-400'}`}>{p.side?.toUpperCase()}</Badge></td>
                        <td className="py-2.5 px-3 font-mono text-orange-400 font-bold">{p.leverage}x</td>
                        <td className="py-2.5 px-3 font-mono">${fmtP(p.entry_price)}</td>
                        <td className="py-2.5 px-3 font-mono">${fmtP(p.exit_price)}</td>
                        <td className="py-2.5 px-3 font-mono">${p.margin_usdt?.toFixed(2)}</td>
                        <td className={`py-2.5 px-3 font-mono font-bold ${(p.realized_pnl || 0) >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                          {(p.realized_pnl || 0) >= 0 ? '+' : ''}${p.realized_pnl?.toFixed(2)} ({p.roe_pct?.toFixed(1)}%)
                        </td>
                        <td className="py-2.5 px-3">
                          <Badge className={`text-[9px] border-0 ${p.manual_entry ? 'bg-orange-500/20 text-orange-400' : 'bg-blue-500/20 text-blue-400'}`}>
                            {p.manual_entry ? 'MANUAL' : 'LIVE'}
                          </Badge>
                        </td>
                        <td className="py-2.5 px-3">
                          <Badge className={`text-[9px] border-0 ${p.status === 'liquidated' ? 'bg-red-500/20 text-red-400' : p.status === 'tp_hit' ? 'bg-green-500/20 text-green-400' : p.status === 'sl_hit' ? 'bg-yellow-500/20 text-yellow-400' : 'bg-slate-700 text-slate-300'}`}>
                            {p.status?.toUpperCase().replace('_', ' ')}
                          </Badge>
                        </td>
                        <td className="py-2.5 px-3 text-[10px] text-slate-500">{p.closed_at ? new Date(p.closed_at).toLocaleString() : '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </TabsContent>
          )}
        </Tabs>
      </div>

      {/* Manual Entry Dialog (Admin/Trader only) */}
      <Dialog open={manualDialog} onOpenChange={setManualDialog}>
        <DialogContent className="bg-slate-900 border-slate-700 text-slate-100 max-w-2xl" data-testid="manual-entry-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <Plus size={16} className="text-orange-400" />
              Manual Futures Trade Entry
            </DialogTitle>
            <p className="text-[11px] text-slate-400">Record a closed futures trade (e.g., from Binance/Bybit off-platform). It flows into the client's P&amp;L and Investor Portal trade history.</p>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3 py-2">
            {/* Client */}
            <div className="col-span-2">
              <label className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold mb-1 block">Client *</label>
              <Select value={manualForm.client_id} onValueChange={v => setManualForm({ ...manualForm, client_id: v })}>
                <SelectTrigger className="bg-slate-800 border-slate-700 text-slate-100 h-9 text-xs" data-testid="manual-client-select"><SelectValue placeholder="Select client" /></SelectTrigger>
                <SelectContent className="bg-slate-900 border-slate-700 text-slate-100">
                  {clients.map(c => <SelectItem key={c.id} value={c.id}>{c.client_id} — {c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            {/* Symbol */}
            <div>
              <label className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold mb-1 block">Symbol *</label>
              <Select value={manualForm.symbol} onValueChange={v => setManualForm({ ...manualForm, symbol: v })}>
                <SelectTrigger className="bg-slate-800 border-slate-700 text-slate-100 h-9 text-xs" data-testid="manual-symbol"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-slate-900 border-slate-700 text-slate-100">
                  {symbols.map(s => <SelectItem key={s.symbol} value={s.symbol}>{s.symbol} — {s.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            {/* Side */}
            <div>
              <label className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold mb-1 block">Side *</label>
              <div className="flex gap-1">
                <button type="button" onClick={() => setManualForm({ ...manualForm, side: 'long' })}
                  className={`flex-1 py-2 rounded text-xs font-bold ${manualForm.side === 'long' ? 'bg-green-500 text-white' : 'bg-slate-800 text-slate-400'}`} data-testid="manual-side-long">
                  Long
                </button>
                <button type="button" onClick={() => setManualForm({ ...manualForm, side: 'short' })}
                  className={`flex-1 py-2 rounded text-xs font-bold ${manualForm.side === 'short' ? 'bg-red-500 text-white' : 'bg-slate-800 text-slate-400'}`} data-testid="manual-side-short">
                  Short
                </button>
              </div>
            </div>
            {/* Leverage */}
            <div>
              <label className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold mb-1 block">Leverage (x) *</label>
              <Input type="number" min="1" max="100" value={manualForm.leverage}
                onChange={e => setManualForm({ ...manualForm, leverage: e.target.value })}
                className="bg-slate-800 border-slate-700 text-slate-100 h-9 text-xs font-mono" data-testid="manual-leverage" />
            </div>
            {/* Margin */}
            <div>
              <label className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold mb-1 block">Margin (USDT) *</label>
              <Input type="number" step="any" value={manualForm.margin_usdt}
                onChange={e => setManualForm({ ...manualForm, margin_usdt: e.target.value })}
                placeholder="e.g. 500" className="bg-slate-800 border-slate-700 text-slate-100 h-9 text-xs font-mono" data-testid="manual-margin" />
            </div>
            {/* Entry */}
            <div>
              <label className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold mb-1 block">Entry Price *</label>
              <Input type="number" step="any" value={manualForm.entry_price}
                onChange={e => setManualForm({ ...manualForm, entry_price: e.target.value })}
                placeholder="e.g. 68500" className="bg-slate-800 border-slate-700 text-slate-100 h-9 text-xs font-mono" data-testid="manual-entry" />
            </div>
            {/* Exit */}
            <div>
              <label className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold mb-1 block">Exit Price *</label>
              <Input type="number" step="any" value={manualForm.exit_price}
                onChange={e => setManualForm({ ...manualForm, exit_price: e.target.value })}
                placeholder="e.g. 70500" className="bg-slate-800 border-slate-700 text-slate-100 h-9 text-xs font-mono" data-testid="manual-exit" />
            </div>
            {/* Dates */}
            <div>
              <label className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold mb-1 block">Opened At (optional)</label>
              <Input type="datetime-local" value={manualForm.opened_at}
                onChange={e => setManualForm({ ...manualForm, opened_at: e.target.value })}
                className="bg-slate-800 border-slate-700 text-slate-100 h-9 text-xs" data-testid="manual-opened-at" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold mb-1 block">Closed At (optional)</label>
              <Input type="datetime-local" value={manualForm.closed_at}
                onChange={e => setManualForm({ ...manualForm, closed_at: e.target.value })}
                className="bg-slate-800 border-slate-700 text-slate-100 h-9 text-xs" data-testid="manual-closed-at" />
            </div>
            {/* P&L mode */}
            <div className="col-span-2">
              <label className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold mb-1 block">P&amp;L Calculation</label>
              <div className="flex gap-2">
                <button type="button" onClick={() => setManualForm({ ...manualForm, pnl_mode: 'auto' })}
                  className={`px-3 py-1.5 rounded text-xs font-semibold ${manualForm.pnl_mode === 'auto' ? 'bg-orange-500 text-white' : 'bg-slate-800 text-slate-400'}`} data-testid="pnl-mode-auto">
                  Auto (from entry/exit)
                </button>
                <button type="button" onClick={() => setManualForm({ ...manualForm, pnl_mode: 'manual' })}
                  className={`px-3 py-1.5 rounded text-xs font-semibold ${manualForm.pnl_mode === 'manual' ? 'bg-orange-500 text-white' : 'bg-slate-800 text-slate-400'}`} data-testid="pnl-mode-manual">
                  Manual override
                </button>
              </div>
              {manualForm.pnl_mode === 'manual' && (
                <Input type="number" step="any" value={manualForm.realized_pnl}
                  onChange={e => setManualForm({ ...manualForm, realized_pnl: e.target.value })}
                  placeholder="Realized P&L (USDT, use negative for loss)"
                  className="mt-2 bg-slate-800 border-slate-700 text-slate-100 h-9 text-xs font-mono" data-testid="manual-realized-pnl" />
              )}
            </div>
            {/* Preview */}
            <div className="col-span-2 bg-slate-950 rounded p-3 border border-slate-800" data-testid="manual-preview">
              <p className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-2">Preview</p>
              <div className="grid grid-cols-3 gap-3 text-[11px]">
                <div>
                  <p className="text-slate-500">Quantity</p>
                  <p className="font-mono font-bold">{manualPnlPreview.qty ? manualPnlPreview.qty.toFixed(6) : '—'} {manualForm.symbol}</p>
                </div>
                <div>
                  <p className="text-slate-500">Notional Size</p>
                  <p className="font-mono font-bold">${(parseFloat(manualForm.margin_usdt) * parseInt(manualForm.leverage || 1) || 0).toFixed(2)}</p>
                </div>
                <div>
                  <p className="text-slate-500">Realized P&amp;L (ROE%)</p>
                  <p className={`font-mono font-bold ${(manualForm.pnl_mode === 'manual' ? parseFloat(manualForm.realized_pnl || 0) : manualPnlPreview.pnl) >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                    {(manualForm.pnl_mode === 'manual' ? parseFloat(manualForm.realized_pnl || 0) : manualPnlPreview.pnl) >= 0 ? '+' : ''}
                    ${(manualForm.pnl_mode === 'manual' ? parseFloat(manualForm.realized_pnl || 0) : manualPnlPreview.pnl).toFixed(2)}
                    {manualForm.pnl_mode === 'auto' && ` (${manualPnlPreview.roe.toFixed(1)}%)`}
                  </p>
                </div>
              </div>
            </div>
            {/* Toggles */}
            <div className="col-span-2 space-y-2 bg-slate-800/40 rounded p-3">
              <label className="flex items-center gap-2 text-xs cursor-pointer">
                <input type="checkbox" checked={manualForm.affect_balance}
                  onChange={e => setManualForm({ ...manualForm, affect_balance: e.target.checked })}
                  className="accent-orange-500" data-testid="toggle-affect-balance" />
                <span>Credit/Debit client's <b>Futures Wallet</b> (uncheck for record-only)</span>
              </label>
              <label className="flex items-center gap-2 text-xs cursor-pointer">
                <input type="checkbox" checked={manualForm.auto_distribute}
                  onChange={e => setManualForm({ ...manualForm, auto_distribute: e.target.checked })}
                  className="accent-orange-500" data-testid="toggle-auto-distribute" />
                <span>Auto profit distribution (client 50% / trader 20% / company 25% / manager 5%)</span>
              </label>
            </div>
            {/* Notes */}
            <div className="col-span-2">
              <label className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold mb-1 block">Notes</label>
              <Input value={manualForm.notes} onChange={e => setManualForm({ ...manualForm, notes: e.target.value })}
                placeholder="e.g. Binance trade ID: 123, closed manually"
                className="bg-slate-800 border-slate-700 text-slate-100 h-9 text-xs" data-testid="manual-notes" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setManualDialog(false)} className="border-slate-700 text-slate-300 hover:bg-slate-800"><X size={12} className="mr-1" /> Cancel</Button>
            <Button onClick={handleManualSubmit} disabled={executing || !manualForm.client_id || !manualForm.entry_price || !manualForm.exit_price || !manualForm.margin_usdt}
              className="bg-orange-500 hover:bg-orange-600 text-white" data-testid="manual-submit-btn">
              {executing ? <Loader2 size={12} className="animate-spin" /> : <><FileText size={12} className="mr-1" /> Record Trade</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const StatTile = ({ label, value, tone }) => (
  <div className="bg-slate-900 border border-slate-800 rounded-lg p-2">
    <p className="text-[9px] uppercase tracking-widest text-slate-400 font-bold">{label}</p>
    <p className={`font-mono font-bold text-sm mt-0.5 ${tone === 'ok' ? 'text-green-400' : tone === 'bad' ? 'text-red-400' : 'text-slate-100'}`}>{value}</p>
  </div>
);
